import React, { useEffect, useMemo, useState } from "react";
import axios from "../../api/axiosInstance";
import * as XLSX from "xlsx";
import { filterRowsForCurrentScope, getCenterScopedPath } from "../../utils/centerScope";
import useDataStore from "../../store/dataStore";

const AttendanceManagementPage = () => {

    const BASE_URL = "https://mis.empowerplus.in";

    const store = useDataStore();
    const CACHE_KEY = "attendance";

    const [attendance, setAttendance] = useState(() => store.getRows(CACHE_KEY));
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);

    const [form, setForm] = useState({
        attendanceDate: "",
        beneficiaryId: "",
        status: "Present",
        centerId: "",
        batchId: "",
        projectId: ""
    });

    const [filterDate, setFilterDate] = useState("");
    const [visibleCount, setVisibleCount] = useState(100);
    const [allLoaded, setAllLoaded] = useState(false);
    const [loadingAll, setLoadingAll] = useState(false);

    const normalizeRows = (value) => {
        if (Array.isArray(value)) return value;
        if (Array.isArray(value?.content)) return value.content;
        return [];
    };

    // ================= Fetch First 100 =================

    const fetchFirst100 = async () => {
        if (store.isCached(CACHE_KEY)) {
            setAttendance(store.getRows(CACHE_KEY));
            setAllLoaded(store.isAllLoaded(CACHE_KEY));
            return;
        }

        const requestPaths = [
            getCenterScopedPath("/api/attendance"),
            "/api/attendance",
            "/api/attendance/all"
        ].filter((value, index, arr) => value && arr.indexOf(value) === index);

        for (const path of requestPaths) {
            try {
                const paginated = path + (path.includes("?") ? "&" : "?") + "page=0&size=100";
                const res = await axios.get(paginated.startsWith("http") ? paginated : `${BASE_URL}${paginated}`);
                const data = filterRowsForCurrentScope(normalizeRows(res.data));
                store.setRows(CACHE_KEY, data, false);
                setAttendance(data);
                setAllLoaded(false);
                return;
            } catch (error) {
                console.error("Attendance fetch failed for", path, error);
            }
        }

        setAttendance([]);
        alert("Failed to fetch attendance data");
    };

    // ================= Fetch All Attendance =================

    const fetchAllAttendance = async () => {
        setLoadingAll(true);
        try {
            const requestPaths = [
                getCenterScopedPath("/api/attendance"),
                "/api/attendance",
                "/api/attendance/all"
            ].filter((value, index, arr) => value && arr.indexOf(value) === index);

            for (const path of requestPaths) {
                try {
                    const res = await axios.get(path.startsWith("http") ? path : `${BASE_URL}${path}`);
                    const data = filterRowsForCurrentScope(normalizeRows(res.data));
                    store.setRows(CACHE_KEY, data, true);
                    setAttendance(data);
                    setAllLoaded(true);
                    return;
                } catch (error) {
                    console.error("Attendance fetch failed for", path, error);
                }
            }

            alert("Failed to fetch all attendance data");
        } finally {
            setLoadingAll(false);
        }
    };

    useEffect(() => {
        fetchFirst100();
    }, []);

    // ================= Handle Form =================

    const handleChange = (e) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value
        });
    };

    // ================= Add Attendance =================

    const addAttendance = async (e) => {
        e.preventDefault();

        await axios.post(`${BASE_URL}/api/attendance`, form);

        alert("Attendance added");

        setForm({
            attendanceDate: "",
            beneficiaryId: "",
            status: "Present",
            centerId: "",
            batchId: "",
            projectId: ""
        });

        store.invalidate(CACHE_KEY);
        fetchFirst100();
    };

    // ================= Upload Excel =================

    const CHUNK_SIZE = 500;
    const PARALLEL = 2;

    const uploadExcel = async () => {
        if (!file) return alert("Please select a file");
        let headerRow, dataRows;
        try {
            const buffer = await file.arrayBuffer();
            const wb = XLSX.read(buffer, { type: "array" });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
            headerRow = raw[0];
            dataRows = raw.slice(1);
        } catch (err) {
            return alert(`Cannot read file: ${err.message}`);
        }
        if (!dataRows.length) return alert("Excel file is empty");
        const totalRows = dataRows.length;
        const totalChunks = Math.ceil(totalRows / CHUNK_SIZE);
        setUploading(true);
        setUploadProgress({ uploaded: 0, total: totalRows, chunk: 0, totalChunks });
        let uploadedSoFar = 0;
        let totalInserted = 0;
        let totalSkipped = 0;
        let failed = false;
        let firstError = "";
        const uploadOneChunk = async (i) => {
            const chunkRows = [headerRow, ...dataRows.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE)];
            const res = await axios.post(`${BASE_URL}/api/attendance/upload-json`, chunkRows);
            return { inserted: res.data?.inserted ?? 0, skipped: res.data?.skipped ?? 0 };
        };
        for (let batchStart = 0; batchStart < totalChunks && !failed; batchStart += PARALLEL) {
            const batchEnd = Math.min(batchStart + PARALLEL, totalChunks);
            const batchIndices = Array.from({ length: batchEnd - batchStart }, (_, k) => batchStart + k);
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks });
            const results = await Promise.allSettled(batchIndices.map(uploadOneChunk));
            for (const result of results) {
                if (result.status === "fulfilled") {
                    totalInserted += result.value.inserted;
                    totalSkipped += result.value.skipped;
                    uploadedSoFar += result.value.inserted + result.value.skipped;
                } else {
                    failed = true;
                    firstError = result.reason?.message || "Upload failed";
                }
            }
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks, error: failed });
        }
        setUploading(false);
        if (!failed) {
            alert(`Upload complete — Inserted: ${totalInserted.toLocaleString()}, Skipped: ${totalSkipped.toLocaleString()} (${totalChunks} chunks)`);
            setFile(null);
            setUploadProgress(null);
            store.invalidate(CACHE_KEY);
            fetchFirst100();
        } else {
            alert(firstError);
            setUploadProgress(prev => prev ? { ...prev, error: true } : null);
        }
    };

    // ================= Filter by Date =================

    const filterAttendance = async () => {
        if (!filterDate) {
            alert("Please select a date first");
        }
    };

    // ================= Delete =================

    const deleteAttendance = async (id) => {

        if (!window.confirm("Delete this attendance?")) return;

        await axios.delete(`${BASE_URL}/api/attendance/${id}`);

        store.invalidate(CACHE_KEY);
        fetchFirst100();
    };


    const [exporting, setExporting] = useState(false);

    const exportExcel = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            let data = attendance;
            if (!allLoaded) {
                const path = getCenterScopedPath("/api/attendance") || "/api/attendance";
                const res = await axios.get(`${BASE_URL}${path}`);
                const all = filterRowsForCurrentScope(normalizeRows(res.data));
                data = all;
                setAttendance(all);
                setAllLoaded(true);
                store.setRows(CACHE_KEY, all, true);
            }
            if (!data.length) return alert("No data to export");
            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Attendance");
            XLSX.writeFile(wb, "attendance.xlsx");
        } catch (e) {
            alert("Export failed: " + e.message);
        } finally {
            setExporting(false);
        }
    };

    const downloadTemplate = () => {

        const link = document.createElement("a");

        link.href = "/templates/attendance_template.xlsx";

        link.download = "attendance_template.xlsx";

        document.body.appendChild(link);

        link.click();

        document.body.removeChild(link);
    };

    useEffect(() => { setVisibleCount(100); }, [filterDate]);

    const visibleAttendance = useMemo(() => {
        if (!filterDate) {
            return attendance;
        }

        return attendance.filter((row) => String(row.attendanceDate || "").slice(0, 10) === filterDate);
    }, [attendance, filterDate]);


    return (
        <div className="container mt-4">

            <h3>Attendance Management</h3>

            {/* ================= Excel Upload ================= */}

            <div className="card p-3 mb-4">

                <h5>Upload Attendance Excel</h5>

                <div className="d-flex gap-2">

                    <input
                        type="file"
                        accept=".xlsx,.xls"
                        className="form-control"
                        onChange={(e) => setFile(e.target.files[0])}
                    />

                    <button
                        className="btn btn-success"
                        onClick={uploadExcel}
                    >
                        Upload Excel
                    </button>

                    <button
                        className="btn btn-outline-secondary"
                        onClick={downloadTemplate}
                    >
                        Download Excel Template
                    </button>

                </div>

            </div>

            {/* ================= Add Attendance ================= */}

            {/* <div className="card p-3 mb-4">

                <h5>Mark Attendance</h5>

                <form onSubmit={addAttendance}>

                    <div className="row">

                        <div className="col-md-2">
                            <input
                                type="date"
                                name="attendanceDate"
                                className="form-control"
                                value={form.attendanceDate}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="col-md-2">
                            <input
                                type="text"
                                name="beneficiaryId"
                                placeholder="Beneficiary ID"
                                className="form-control"
                                value={form.beneficiaryId}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="col-md-2">
                            <select
                                name="status"
                                className="form-control"
                                value={form.status}
                                onChange={handleChange}
                            >
                                <option>Present</option>
                                <option>Absent</option>
                            </select>
                        </div>

                        <div className="col-md-2">
                            <input
                                type="text"
                                name="centerId"
                                placeholder="Center ID"
                                className="form-control"
                                value={form.centerId}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="col-md-2">
                            <input
                                type="text"
                                name="batchId"
                                placeholder="Batch ID"
                                className="form-control"
                                value={form.batchId}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="col-md-2">
                            <input
                                type="text"
                                name="projectId"
                                placeholder="Project ID"
                                className="form-control"
                                value={form.projectId}
                                onChange={handleChange}
                            />
                        </div>

                    </div>

                    <button className="btn btn-primary mt-3">
                        Save Attendance
                    </button>

                </form>

            </div> */}

            {/* ================= Filter ================= */}

            <div className="mb-3 d-flex">

                <input
                    type="date"
                    className="form-control w-25"
                    value={filterDate}
                    onChange={(e) => setFilterDate(e.target.value)}
                />

                <button
                    className="btn btn-secondary ms-2"
                    onClick={filterAttendance}
                    disabled={!filterDate}
                >
                    Filter
                </button>

                <button
                    className="btn btn-outline-dark ms-2"
                    onClick={fetchFirst100}
                >
                    Reset
                </button>

                <button
                    className="btn btn-outline-success ms-2"
                    onClick={exportExcel}
                    disabled={exporting}
                >
                    {exporting ? "Processing..." : "Export All Data"}
                </button>

            </div>

            {/* ================= Table ================= */}

            <div className="table-responsive">

                <table className="table table-bordered table-striped">

                    <thead className="table-dark">

                        <tr>
                            <th>#</th>
                            <th>Date</th>
                            <th>Beneficiary ID</th>
                            <th>Name</th>
                            <th>Status</th>
                            <th>Center</th>
                            <th>Batch</th>
                            <th>Project</th>
                            <th>Action</th>
                        </tr>

                    </thead>

                    <tbody>

                        {visibleAttendance.slice(0, visibleCount).map((a, i) => (

                            <tr key={a.id}>

                                <td>{i + 1}</td>
                                <td>{a.attendanceDate}</td>
                                <td>{a.beneficiaryId}</td>
                                <td>{a.beneficiaryName}</td>
                                <td>{a.status}</td>
                                <td>{a.centerId}</td>
                                <td>{a.batchId}</td>
                                <td>{a.projectId}</td>

                                <td>

                                    <button
                                        className="btn btn-danger btn-sm"
                                        onClick={() => deleteAttendance(a.id)}
                                    >
                                        Delete
                                    </button>

                                </td>

                            </tr>

                        ))}

                        {!visibleAttendance.length && (
                            <tr>
                                <td colSpan="9" className="text-center text-muted">
                                    {filterDate ? "No attendance found for the selected date" : "No attendance records found"}
                                </td>
                            </tr>
                        )}
                        {!allLoaded && !loadingAll && (
                            <tr>
                                <td colSpan="9" style={{ textAlign: "center", padding: "14px" }}>
                                    <button className="btn btn-outline" onClick={fetchAllAttendance} disabled={loadingAll}>
                                        {loadingAll ? "Loading all data…" : "Load All Data"}
                                    </button>
                                </td>
                            </tr>
                        )}
                        {loadingAll && (
                            <tr>
                                <td colSpan="9" style={{ textAlign: "center", padding: "14px" }}>
                                    Loading all data…
                                </td>
                            </tr>
                        )}
                        {visibleAttendance.length > visibleCount && (
                            <tr>
                                <td colSpan="9" style={{ textAlign: "center", padding: "14px" }}>
                                    <button
                                        className="btn btn-outline"
                                        onClick={() => setVisibleCount((c) => c + 100)}
                                    >
                                        Load More ({visibleAttendance.length - visibleCount} remaining)
                                    </button>
                                </td>
                            </tr>
                        )}

                    </tbody>

                </table>

            </div>

        </div>
    );
};

export default AttendanceManagementPage;


// import React, { useEffect, useMemo, useState } from "react";
// import { createPortal } from "react-dom";
// import * as XLSX from "xlsx";

// const BASE_URL = "https://mis.empowerplus.in";

// export default function AttendanceManagementPage() {
//     const token = localStorage.getItem("token");

//     /* ================= COMMON ================= */
//     const [msg, setMsg] = useState({ type: "", text: "" });
//     const flash = (type, text) => {
//         setMsg({ type, text });
//         setTimeout(() => setMsg({ type: "", text: "" }), 3000);
//     };

//     const authHeaders = useMemo(
//         () => ({ Authorization: `Bearer ${token}` }),
//         [token]
//     );

//     const jsonHeaders = useMemo(
//         () => ({
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json",
//         }),
//         [token]
//     );

//     const safeJson = async (res) => {
//         const t = await res.text();
//         try {
//             return t ? JSON.parse(t) : [];
//         } catch {
//             return [];
//         }
//     };

//     /* ================= EXCEL UPLOAD ================= */
//     const [file, setFile] = useState(null);
//     const [uploading, setUploading] = useState(false);

//     const uploadExcel = async () => {
//         if (!file) return flash("err", "Select Excel file");

//         const fd = new FormData();
//         fd.append("file", file);

//         setUploading(true);
//         try {
//             await fetch(`${BASE_URL}/api/attendance/upload?actor=Rahul`, {
//                 method: "POST",
//                 headers: authHeaders,
//                 body: fd,
//             });
//             flash("ok", "Attendance uploaded");
//             setFile(null);
//             fetchAttendance();
//         } catch {
//             flash("err", "Upload failed");
//         } finally {
//             setUploading(false);
//         }
//     };

//     /* ================= TABLE ================= */
//     const [rows, setRows] = useState([]);
//     const [loading, setLoading] = useState(false);
//     const [search, setSearch] = useState("");

//     const fetchAttendance = async () => {
//         setLoading(true);
//         try {
//             const res = await fetch(`${BASE_URL}/api/attendance`, {
//                 headers: authHeaders,
//             });
//             const data = await safeJson(res);
//             setRows(Array.isArray(data) ? data : []);
//         } catch {
//             setRows([]);
//         } finally {
//             setLoading(false);
//         }
//     };

//     useEffect(() => {
//         fetchAttendance();
//     }, []);

//     const filtered = useMemo(() => {
//         const q = search.toLowerCase();
//         return rows.filter(
//             (r) =>
//                 r.beneficiaryId?.toLowerCase().includes(q) ||
//                 r.status?.toLowerCase().includes(q)
//         );
//     }, [rows, search]);

//     /* ================= EDIT ================= */
//     const emptyForm = {
//         beneficiaryId: "",
//         markForDate: "",
//         status: "",
//         updateDate: "",
//     };

//     const [editingKey, setEditingKey] = useState(null);
//     const [form, setForm] = useState(emptyForm);

//     const openEdit = (row) => {
//         setEditingKey(`${row.beneficiaryId}_${row.markForDate}`);
//         setForm({
//             ...emptyForm,
//             ...row,
//         });
//     };

//     const updateAttendance = async () => {
//         await fetch(
//             `${BASE_URL}/attendance?beneficiaryId=${encodeURIComponent(
//                 form.beneficiaryId
//             )}&markForDate=${form.markForDate}&actor=Rahul`,
//             {
//                 method: "PUT",
//                 headers: jsonHeaders,
//                 body: JSON.stringify({
//                     status: form.status,
//                     updateDate: form.updateDate,
//                 }),
//             }
//         );
//         flash("ok", "Attendance updated");
//         setEditingKey(null);
//         fetchAttendance();
//     };

//     /* ================= DELETE ================= */
//     const deleteAttendance = async (row) => {
//         if (!window.confirm("Deactivate attendance?")) return;

//         await fetch(
//             `${BASE_URL}/attendance?beneficiaryId=${encodeURIComponent(
//                 row.beneficiaryId
//             )}&markForDate=${row.markForDate}&actor=Rahul`,
//             {
//                 method: "DELETE",
//                 headers: authHeaders,
//             }
//         );
//         flash("ok", "Attendance deactivated");
//         fetchAttendance();
//     };

//     /* ================= AUDIT ================= */
//     const [auditOpen, setAuditOpen] = useState(false);
//     const [auditRows, setAuditRows] = useState([]);
//     const [auditBeneficiary, setAuditBeneficiary] = useState("");

//     const openAudit = async (beneficiaryId) => {
//         setAuditOpen(true);
//         setAuditBeneficiary(beneficiaryId);

//         const res = await fetch(
//             `${BASE_URL}/attendance/audits/by-beneficiary?beneficiaryId=${encodeURIComponent(
//                 beneficiaryId
//             )}`,
//             { headers: authHeaders }
//         );

//         setAuditRows(await safeJson(res));
//     };

//     const downloadTemplate = () => {
//         const a = document.createElement("a");
//         a.href = "/templates/attendance_template.xlsx"; // local path
//         a.download = "attendance_template.xlsx";
//         document.body.appendChild(a);
//         a.click();
//         a.remove();
//     };

//     const exportExcel = () => {
//         const ws = XLSX.utils.json_to_sheet(filtered);
//         const wb = XLSX.utils.book_new();
//         XLSX.utils.book_append_sheet(wb, ws, "Attendance");
//         XLSX.writeFile(wb, "attendance.xlsx");
//     };



//     /* ================= UI ================= */
//     return (
//         <div className="cmp-page">
//             <h1>Attendance Management</h1>

//             {msg.text && (
//                 <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`}>
//                     {msg.text}
//                 </div>
//             )}

//             {/* Upload */}
//             <div className="card grid-3">

//                 <input type="file" onChange={(e) => setFile(e.target.files[0])} style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 12px", background: "#fff" }} />

//                 <button className="btn btn-primary" onClick={uploadExcel}>
//                     {uploading ? "Uploading..." : "Upload Excel"}
//                 </button>
//                 <button className="btn btn-outline" onClick={downloadTemplate}>
//                     Download Excel Template
//                 </button>
//             </div>

//             {/* Table */}
//             <div className="card">
//                 <div className="table-actions" style={{ marginTop: "1rem" }}>
//                     <input
//                         className="search"
//                         placeholder="Search beneficiary..."
//                         style={{ marginRight: "1rem" }}
//                         value={search}
//                         onChange={(e) => setSearch(e.target.value)}
//                     />

//                     <button className="btn" onClick={exportExcel}>
//                         Export Excel
//                     </button>
//                 </div>

//                 <div className="table-wrap">
//                     <table className="ep-table">
//                         <thead>
//                             <tr>
//                                 <th>Beneficiary ID</th>
//                                 <th>Beneficiary Name</th>
//                                 <th>Project Name</th>
//                                 <th>Status</th>
//                                 <th>Mark For Date</th>
//                                 <th>Updated On</th>
//                                 <th className="actions">Actions</th>
//                             </tr>
//                         </thead>
//                         <tbody>
//                             {filtered.map((r) => (
//                                 <tr key={`${r.beneficiaryId}_${r.markForDate}`}>
//                                     <td>{r.beneficiaryId}</td>
//                                     <td>{r.beneficiaryName}</td>
//                                     <td>{r.projectName}</td>
//                                     <td>{r.status}</td>
//                                     <td>{r.markForDate}</td>
//                                     <td>{r.updateDate}</td>
//                                     <td className="actions">
//                                         <button className="btn btn-sm" onClick={() => openEdit(r)} style={{ marginRight: "1rem" }}>
//                                             Edit
//                                         </button>
//                                         {/* <button
//                                             className="btn btn-sm"
//                                             onClick={() => openAudit(r.beneficiaryId)}
//                                         >
//                                             Audit
//                                         </button> */}
//                                         <button
//                                             className="btn btn-sm btn-danger"
//                                             onClick={() => deleteAttendance(r)}
//                                         >
//                                             Deactivate
//                                         </button>
//                                     </td>
//                                 </tr>
//                             ))}
//                             {!filtered.length && (
//                                 <tr>
//                                     <td colSpan="5" className="muted">
//                                         {loading ? "Loading..." : "No records found"}
//                                     </td>
//                                 </tr>
//                             )}
//                         </tbody>
//                     </table>
//                 </div>
//             </div>

//             {/* EDIT MODAL */}
//             {
//                 editingKey &&
//                 createPortal(
//                     <div className="modal-overlay" onClick={() => setEditingKey(null)}>
//                         <div className="ep-modal" onClick={(e) => e.stopPropagation()}>
//                             <h2>Edit Attendance</h2>

//                             <input value={form.beneficiaryId} disabled />
//                             <input value={form.markForDate} disabled />

//                             <select
//                                 value={form.status}
//                                 onChange={(e) =>
//                                     setForm((p) => ({ ...p, status: e.target.value }))
//                                 }
//                             >
//                                 <option value="">Select Status</option>
//                                 <option value="Present">Present</option>
//                                 <option value="Absent">Absent</option>
//                                 <option value="Leave">Leave</option>
//                                 <option value="Holiday">Holiday</option>
//                             </select>

//                             <input
//                                 type="date"
//                                 value={form.updateDate}
//                                 onChange={(e) =>
//                                     setForm((p) => ({ ...p, updateDate: e.target.value }))
//                                 }
//                             />

//                             <div className="modal-actions">
//                                 <button className="btn" onClick={() => setEditingKey(null)}>
//                                     Cancel
//                                 </button>
//                                 <button className="btn btn-primary" onClick={updateAttendance}>
//                                     Save
//                                 </button>
//                             </div>
//                         </div>
//                     </div>,
//                     document.body
//                 )
//             }

//             {/* AUDIT MODAL */}
//             {
//                 auditOpen &&
//                 createPortal(
//                     <div className="modal-overlay" onClick={() => setAuditOpen(false)}>
//                         <div className="ep-modal wide" onClick={(e) => e.stopPropagation()}>
//                             <h2>Attendance Audit Logs</h2>
//                             <pre>{JSON.stringify(auditRows, null, 2)}</pre>
//                         </div>
//                     </div>,
//                     document.body
//                 )
//             }

//             {/* INLINE CSS */}
//             <style>{`
//             .input-file {
//                  border: "1px solid #e5e7eb",
//         borderRadius: 10,
//         padding: "10px 12px",
//         background: "#fff",}

//       .cmp-page { padding:22px; background:#f7f7fb; min-height:100vh; font-family:Inter }
//       h1 { font-size:26px; font-weight:900 }

//       .flash { padding:10px; border-radius:10px; margin:10px 0; font-weight:700 }
//       .flash-ok { background:#ecfdf5; color:#065f46 }
//       .flash-err { background:#fef2f2; color:#991b1b }

//       .card { background:#fff; border:1px solid #e5e7eb; border-radius:14px; padding:16px; margin-bottom:14px }
//       .grid-3 { display:grid; grid-template-columns:1fr auto auto; gap:10px }

//       .btn { border:1px solid #e5e7eb; padding:10px 14px; border-radius:12px; font-weight:800; cursor:pointer; background:#fff }
//       .btn-primary { background:#2563eb; color:#fff }
//       .btn-sm { padding:6px 10px; font-size:12px }
//       .btn-danger { color:#991b1b; border-color:#fca5a5 }

//       .search { margin-bottom:10px; padding:10px; border-radius:12px; border:1px solid #e5e7eb }

//       .table-wrap { overflow-x:auto }
//       .ep-table { width:100%; min-width:800px; border-collapse:collapse }
//       th, td { padding:10px; border-bottom:1px solid #e5e7eb; font-size:13px; white-space:nowrap }
//       th { background:#fcfcff; text-align:left }
//       td.actions { position:sticky; right:0; background:#fff }

//       .muted { color:#6b7280; font-weight:700 }

//       .modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); display:flex; align-items:center; justify-content:center; z-index:99999 }
//       .ep-modal { background:#fff; padding:18px; border-radius:16px; width:520px; max-height:90vh; overflow:auto }
//       .ep-modal.wide { width:900px }
//       .ep-modal input, .ep-modal select {
//         width:100%; padding:10px; margin-bottom:10px;
//         border-radius:12px; border:1px solid #e5e7eb
//       }
//       .modal-actions { display:flex; justify-content:flex-end; gap:10px }
//       `}</style>
//         </div >
//     );
// }
